import { type ReactNode, useCallback, useEffect, useRef } from 'react';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { ContainerAvatar } from '@atlaskit/navigation-system/side-nav-items/container-avatar';
import {
  ExpandableMenuItem,
  ExpandableMenuItemContent,
  ExpandableMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/expandable-menu-item';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';
import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import {
  MenuSection,
  MenuSectionHeading,
} from '@atlaskit/navigation-system/side-nav-items/menu-section';
import { Inline, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';

import { useSetState } from 'src/hooks/set-state';
import { useLocation } from 'src/router/hooks';

import { ExpandableAppFolder } from './apps-folder';
import {
  MAX_VISIBLE_RECENTS,
  MAX_VISIBLE_STARRED,
  REPO_SETTINGS_ID,
  PROJECT_SETTINGS_ID,
  SIDE_NAV_VIEW_MODES,
  type SideNavViewMode,
  DEFAULT_SIDE_NAV_VIEW_MODE,
} from './constants';
import {
  ExpandableItemData,
  StarredObjectType,
  useShowStarredObjects,
} from './hooks';
import { getIconForMenuItem, getLinkItemAfterContent } from './icons';
import { MenuItemDropdownMenu } from './menu-item-dropdown-menu';
import { isMenuItemSelected } from './utils';

const skeletonInlineStyles = xcss({
  paddingBlock: 'space.100',
  marginInlineStart: 'space.100',
});

const MenuItemSkeleton = ({ count }: { count: number }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <Inline
        key={index}
        alignBlock="center"
        space="space.100"
        xcss={skeletonInlineStyles}
      >
        <Skeleton width="16px" height="16px" borderRadius="50%" isShimmering />
        <Skeleton width="100%" height="16px" isShimmering />
      </Inline>
    ))}
  </>
);

type SharedProps<T> = {
  allItemsUrl: string;
  getItemUUID: (item: T) => string;
  getItemAvatarUrl: (item: T) => string;
  getItemName: (item: T) => string;
  getItemPrivate: (item: T) => boolean | undefined;
  objectType: StarredObjectType;
  viewAllItemsLabel: string;
};

type LegacyProps<T> = SharedProps<T> & {
  expandableItems: ExpandableItemData<T>['items'];
  isRecentsLoading: boolean;
};

type StarredProps<T> = SharedProps<T> & {
  starredItems: ExpandableItemData<T>['items'];
  recentItems: ExpandableItemData<T>['items'];
  isRecentsLoading: boolean;
  isStarredLoading: boolean;
  /** Whether to show starred features (starred section and star/unstar actions). False for anonymous users. */
  showStarredFeatures: boolean;
  /** Whether the onboarding spotlight is currently active. Only used by RepositoryNavigationItem. */
  isOnboardingActive?: boolean;
  /** Callback to persist spotlight dismissal. Only used by RepositoryNavigationItem. */
  dismissOnboarding?: () => void;
  viewMode?: SideNavViewMode;
};

export const useExpandedMenuItems = (
  currentItemKey?: string,
  preloadExpandedItem?: () => void
) => {
  const {
    set: expandedMenuItems,
    add: addExpandedMenuItem,
    remove: removeExpandedMenuItem,
  } = useSetState<string>(currentItemKey ? [currentItemKey] : []);
  const preloadedItemKeyRef = useRef<string>();
  // Keep the latest callback without letting callback identity changes retrigger
  // the "expand current item" effect below.
  const preloadExpandedItemRef = useRef(preloadExpandedItem);
  const canPreloadExpandedItem = Boolean(preloadExpandedItem);

  useEffect(() => {
    preloadExpandedItemRef.current = preloadExpandedItem;
  }, [preloadExpandedItem]);

  useEffect(() => {
    // If the current item changes, ensure it's expanded
    if (currentItemKey) {
      addExpandedMenuItem(currentItemKey);
    }
  }, [currentItemKey, addExpandedMenuItem]);

  useEffect(() => {
    // Auto-load only once per current item key. This stays separate from the
    // expansion effect so a new loadNavigation callback does not re-expand an
    // item the user has already collapsed.
    if (
      !currentItemKey ||
      !canPreloadExpandedItem ||
      preloadedItemKeyRef.current === currentItemKey
    ) {
      return;
    }

    preloadedItemKeyRef.current = currentItemKey;
    preloadExpandedItemRef.current?.();
  }, [currentItemKey, canPreloadExpandedItem]);

  return {
    expandedMenuItems,
    addExpandedMenuItem,
    removeExpandedMenuItem,
  };
};

/**
 * Original expandable navigation menu item. Renders only recent items in a
 * single MenuSection. Used when the starred objects feature flag is OFF to
 * preserve the exact existing DOM structure and behavior.
 */
const LegacyExpandableNavigationMenuItem = <
  V extends BB.Repository | BB.Project
>({
  allItemsUrl,
  expandableItems,
  isRecentsLoading,
  getItemUUID,
  getItemPrivate,
  getItemAvatarUrl,
  getItemName,
  viewAllItemsLabel,
  objectType,
}: LegacyProps<V>) => {
  const currentItem = expandableItems.find(item => item.isActive);
  const { expandedMenuItems, addExpandedMenuItem, removeExpandedMenuItem } =
    useExpandedMenuItems(currentItem && getItemUUID(currentItem.item));

  const { pathname } = useLocation();
  // Add one to skeleton count to account for "more items" menu item
  const skeletonCountModifier = 0;
  const skeletonCount =
    Math.min(
      // default is the number of max items
      MAX_VISIBLE_RECENTS,
      // if we already have some items, show fewer skeletons
      Math.max(MAX_VISIBLE_RECENTS - expandableItems.length, 0)
    ) + skeletonCountModifier;

  return (
    <MenuList>
      <MenuSection isMenuListItem>
        <MenuSectionHeading>Recent</MenuSectionHeading>
        <MenuList>
          {expandableItems
            .slice(0, MAX_VISIBLE_RECENTS)
            .map(({ isActive, item, menuItems, ...itemProps }) => (
              <ExpandableMenuItem
                key={getItemUUID(item)}
                isExpanded={expandedMenuItems.has(getItemUUID(item))}
                onExpansionToggle={isExpanded => {
                  if (isExpanded) {
                    addExpandedMenuItem(getItemUUID(item));
                  } else {
                    removeExpandedMenuItem(getItemUUID(item));
                  }
                }}
              >
                <ExpandableMenuItemTrigger
                  onClick={itemProps.loadNavigation}
                  elemBefore={<ContainerAvatar src={getItemAvatarUrl(item)} />}
                  elemAfter={
                    getItemPrivate(item) ? (
                      <LockLockedIcon
                        label="Private"
                        spacing="spacious"
                        size="small"
                      />
                    ) : null
                  }
                >
                  {getItemName(item)}
                </ExpandableMenuItemTrigger>
                <ExpandableMenuItemContent>
                  {itemProps.isLoading ? (
                    <MenuItemSkeleton count={MAX_VISIBLE_RECENTS} />
                  ) : (
                    <>
                      {(menuItems ?? [])
                        .filter(i => i.type === 'menu_item')
                        .map(menuItem => (
                          <LinkMenuItem
                            key={menuItem.id}
                            href={menuItem.url}
                            elemAfter={getLinkItemAfterContent(menuItem)}
                            elemBefore={getIconForMenuItem(menuItem)}
                            isSelected={
                              // isActive refers to the repository/project so we
                              // don't highlight the same navigation item for each
                              // parent item
                              isActive && isMenuItemSelected(menuItem, pathname)
                            }
                          >
                            {menuItem.label}
                          </LinkMenuItem>
                        ))}
                      <ExpandableAppFolder
                        appItems={(menuItems ?? []).filter(
                          i =>
                            i.type === 'connect_menu_item' ||
                            i.type === 'forge_menu_item'
                        )}
                      />
                    </>
                  )}
                </ExpandableMenuItemContent>
              </ExpandableMenuItem>
            ))}

          {isRecentsLoading && <MenuItemSkeleton count={skeletonCount} />}

          <LinkMenuItem
            key={`view-all-${objectType}`}
            href={allItemsUrl}
            elemBefore={<AlignTextLeftIcon label="" />}
          >
            {viewAllItemsLabel}
          </LinkMenuItem>
        </MenuList>
      </MenuSection>
    </MenuList>
  );
};

/**
 * Renders starred and recent items in a single flat MenuList so that React
 * can match items by key (UUID) across renders. Without this, starring or
 * unstarring an item would unmount it from one section and remount it in the
 * other, causing the avatar image to reload and flash.
 *
 * For anonymous users (or when the feature flag is off), `starredItems` will be
 * empty and `showStarredFeatures` will be false, so only the "Recent" section is
 * displayed and no star/unstar dropdown menu appears.
 */
const StarredExpandableNavigationMenuItem = <
  V extends BB.Repository | BB.Project
>({
  allItemsUrl,
  starredItems,
  recentItems,
  isRecentsLoading,
  isStarredLoading,
  getItemUUID,
  getItemPrivate,
  getItemAvatarUrl,
  getItemName,
  objectType,
  viewAllItemsLabel,
  showStarredFeatures,
  isOnboardingActive = false,
  dismissOnboarding,
  viewMode = DEFAULT_SIDE_NAV_VIEW_MODE,
}: StarredProps<V>) => {
  const isMostRecentOnly = viewMode === SIDE_NAV_VIEW_MODES.MOST_RECENT_ONLY;
  const mostRecentItem = isMostRecentOnly ? recentItems[0] : undefined;
  // We should preload the most recent item if it has no menu items already and is not loading
  const shouldPreloadMostRecentItem =
    !!mostRecentItem && !mostRecentItem.menuItems && !mostRecentItem.isLoading;
  const currentItem =
    mostRecentItem ??
    starredItems.find(item => item.isActive) ??
    recentItems.find(item => item.isActive);
  const { expandedMenuItems, addExpandedMenuItem, removeExpandedMenuItem } =
    useExpandedMenuItems(
      currentItem && getItemUUID(currentItem.item),
      shouldPreloadMostRecentItem ? mostRecentItem.loadNavigation : undefined
    );

  const { pathname } = useLocation();

  // Only show the starred section if the user can star items (logged-in)
  // and there are starred items to display (or we're still loading them).
  const showStarredSection =
    !isMostRecentOnly &&
    showStarredFeatures &&
    (starredItems.length > 0 || isStarredLoading);

  const visibleStarredItems = starredItems.slice(0, MAX_VISIBLE_STARRED);
  const visibleRecentItems = recentItems.slice(0, MAX_VISIBLE_RECENTS);
  // Keep overview-triggered refetches from flashing extra recent skeletons
  // when the current mode does not visibly expand the recent section.
  const shouldShowRecentLoading =
    isRecentsLoading &&
    (viewMode === SIDE_NAV_VIEW_MODES.STARRED_AND_RECENT ||
      (isMostRecentOnly && visibleRecentItems.length === 0));
  const showRecentSection =
    visibleRecentItems.length > 0 || shouldShowRecentLoading;

  const starredSkeletonCount = Math.min(
    MAX_VISIBLE_STARRED,
    Math.max(MAX_VISIBLE_STARRED - visibleStarredItems.length, 0)
  );
  const recentSkeletonCount = Math.min(
    MAX_VISIBLE_RECENTS,
    Math.max(MAX_VISIBLE_RECENTS - visibleRecentItems.length, 0)
  );

  const settingsId =
    objectType === 'repositories' ? REPO_SETTINGS_ID : PROJECT_SETTINGS_ID;

  const renderMenuItem = (
    {
      isActive,
      item,
      menuItems,
      isLoading,
      loadNavigation,
    }: ExpandableItemData<V>['items'][number],
    skeletonCount: number
  ) => {
    const itemUUID = getItemUUID(item);
    return (
      <ExpandableMenuItem
        key={itemUUID}
        isExpanded={expandedMenuItems.has(itemUUID)}
        onExpansionToggle={expanded =>
          expanded
            ? addExpandedMenuItem(itemUUID)
            : removeExpandedMenuItem(itemUUID)
        }
      >
        <ExpandableMenuItemTrigger
          onClick={loadNavigation}
          elemBefore={<ContainerAvatar src={getItemAvatarUrl(item)} />}
          actionsOnHover={
            showStarredFeatures ? (
              <MenuItemDropdownMenu
                objectType={objectType}
                resource={item}
                menuItems={menuItems}
                isMenuItemsLoading={isLoading}
                loadMenuItems={loadNavigation}
                showOnboarding={isActive && isOnboardingActive}
                onOnboardingDismiss={dismissOnboarding}
              />
            ) : null
          }
          elemAfter={
            getItemPrivate(item) ? (
              <LockLockedIcon label="Private" spacing="spacious" size="small" />
            ) : null
          }
        >
          {getItemName(item)}
        </ExpandableMenuItemTrigger>
        <ExpandableMenuItemContent>
          {isLoading ? (
            <MenuItemSkeleton count={skeletonCount} />
          ) : (
            <>
              {(menuItems ?? [])
                .filter(i => {
                  if (i.type !== 'menu_item') return false;
                  // if starred FF is enabled we include the settings item in the meatball menu
                  // so we don't need it included in the expandable list
                  if (showStarredFeatures && i.id === settingsId) return false;
                  return true;
                })
                .map(menuItem => (
                  <LinkMenuItem
                    key={menuItem.id}
                    href={menuItem.url}
                    elemAfter={getLinkItemAfterContent(menuItem)}
                    elemBefore={getIconForMenuItem(menuItem)}
                    isSelected={
                      isActive && isMenuItemSelected(menuItem, pathname)
                    }
                  >
                    {menuItem.label}
                  </LinkMenuItem>
                ))}
              <ExpandableAppFolder
                appItems={(menuItems ?? []).filter(
                  i =>
                    i.type === 'connect_menu_item' ||
                    i.type === 'forge_menu_item'
                )}
              />
            </>
          )}
        </ExpandableMenuItemContent>
      </ExpandableMenuItem>
    );
  };

  // Build a single flat array of keyed React elements so that all items share
  // the same key namespace. This lets React match items by UUID key when they
  // move between starred and recent sections.
  const children: ReactNode[] = [];

  if (showStarredSection) {
    children.push(
      <MenuSection isMenuListItem key="starred-items-heading">
        <MenuSectionHeading>Starred</MenuSectionHeading>
      </MenuSection>
    );
  }
  for (const item of visibleStarredItems) {
    children.push(renderMenuItem(item, starredSkeletonCount));
  }
  if (showStarredSection && isStarredLoading && starredSkeletonCount > 0) {
    children.push(
      <MenuItemSkeleton
        key="starred-items-skeleton"
        count={starredSkeletonCount}
      />
    );
  }

  if (showRecentSection && !isMostRecentOnly) {
    children.push(
      <MenuSection isMenuListItem key="recent-items-heading">
        <MenuSectionHeading>Recent</MenuSectionHeading>
      </MenuSection>
    );
  }
  for (const item of visibleRecentItems) {
    children.push(renderMenuItem(item, recentSkeletonCount));
  }
  if (showRecentSection && shouldShowRecentLoading && recentSkeletonCount > 0) {
    children.push(
      <MenuItemSkeleton key="recent-skeleton" count={recentSkeletonCount} />
    );
  }
  children.push(
    <LinkMenuItem
      key={`view-all-${objectType}`}
      href={allItemsUrl}
      elemBefore={<AlignTextLeftIcon label="" />}
    >
      {viewAllItemsLabel}
    </LinkMenuItem>
  );

  return <MenuList>{children}</MenuList>;
};

type ExportedProps<T> = {
  allItemsUrl: string;
  starredItems: ExpandableItemData<T>['items'];
  recentItems: ExpandableItemData<T>['items'];
  isRecentsLoading: boolean;
  isStarredLoading: boolean;
  viewMode?: SideNavViewMode;
};

export const RepositoryNavigationItem = ({
  allItemsUrl,
  starredItems,
  recentItems,
  isRecentsLoading,
  isStarredLoading,
  viewMode = DEFAULT_SIDE_NAV_VIEW_MODE,
  isOnboardingActive,
  dismissOnboarding,
}: ExportedProps<BB.Repository> & {
  // these onboarding props only exist on the repository nav item
  // and are passed down from SideNavContent
  isOnboardingActive?: boolean;
  dismissOnboarding?: () => void;
}) => {
  const getItemUUID = useCallback((repo: BB.Repository) => repo.uuid, []);
  const getItemAvatarUrl = useCallback(
    (repo: BB.Repository) => repo.links.avatar.href,
    []
  );

  const getItemName = useCallback((repo: BB.Repository) => repo.name, []);
  const getItemPrivate = useCallback(
    (repo: BB.Repository) => repo.is_private,
    []
  );

  const { isStarredObjectsEnabled, showStarredObjects } =
    useShowStarredObjects();

  if (isStarredObjectsEnabled) {
    return (
      <StarredExpandableNavigationMenuItem
        allItemsUrl={allItemsUrl}
        starredItems={starredItems}
        recentItems={recentItems}
        isRecentsLoading={isRecentsLoading}
        isStarredLoading={isStarredLoading}
        getItemUUID={getItemUUID}
        getItemAvatarUrl={getItemAvatarUrl}
        getItemName={getItemName}
        getItemPrivate={getItemPrivate}
        objectType="repositories"
        viewAllItemsLabel="View all repositories"
        showStarredFeatures={showStarredObjects}
        isOnboardingActive={isOnboardingActive}
        dismissOnboarding={dismissOnboarding}
        viewMode={viewMode}
      />
    );
  }

  return (
    <LegacyExpandableNavigationMenuItem
      allItemsUrl={allItemsUrl}
      expandableItems={recentItems}
      isRecentsLoading={isRecentsLoading}
      getItemUUID={getItemUUID}
      getItemAvatarUrl={getItemAvatarUrl}
      getItemName={getItemName}
      getItemPrivate={getItemPrivate}
      objectType="repositories"
      viewAllItemsLabel="View all repositories"
    />
  );
};

export const ProjectNavigationItem = ({
  allItemsUrl,
  starredItems,
  recentItems,
  isRecentsLoading,
  isStarredLoading,
  viewMode = DEFAULT_SIDE_NAV_VIEW_MODE,
}: ExportedProps<BB.Project>) => {
  const getItemUUID = useCallback((project: BB.Project) => project.uuid, []);
  const getItemAvatarUrl = useCallback(
    (project: BB.Project) => project.links?.avatar.href ?? '',
    []
  );

  const getItemName = useCallback((project: BB.Project) => project.name, []);
  const getItemPrivate = useCallback(
    (project: BB.Project) => project.is_private,
    []
  );

  const { isStarredObjectsEnabled, showStarredObjects } =
    useShowStarredObjects();

  if (isStarredObjectsEnabled) {
    return (
      <StarredExpandableNavigationMenuItem
        allItemsUrl={allItemsUrl}
        starredItems={starredItems}
        recentItems={recentItems}
        isRecentsLoading={isRecentsLoading}
        isStarredLoading={isStarredLoading}
        getItemUUID={getItemUUID}
        getItemAvatarUrl={getItemAvatarUrl}
        getItemName={getItemName}
        getItemPrivate={getItemPrivate}
        objectType="projects"
        viewAllItemsLabel="View all projects"
        showStarredFeatures={showStarredObjects}
        viewMode={viewMode}
      />
    );
  }

  return (
    <LegacyExpandableNavigationMenuItem
      allItemsUrl={allItemsUrl}
      expandableItems={recentItems}
      isRecentsLoading={isRecentsLoading}
      getItemUUID={getItemUUID}
      getItemAvatarUrl={getItemAvatarUrl}
      getItemName={getItemName}
      getItemPrivate={getItemPrivate}
      objectType="projects"
      viewAllItemsLabel="View all projects"
    />
  );
};
